/**
 * Gutter lightbulb for Monaco code actions.
 *
 * WHY THIS EXISTS
 * ---------------
 * Monaco 0.44 renders its code-action lightbulb as a CONTENT widget
 * (`LightBulbWidget`, contribution id `editor.contrib.lightbulbWidget`) anchored
 * at column 1 of the action's line. Before positioning it, `update()` checks
 * whether the line has enough leading indentation to host the bulb:
 *
 *     const lineHasSpace = fontInfo.spaceWidth * indent > 22;
 *     if (!lineHasSpace) { effectiveLineNumber -= 1; }   // …or += 1
 *
 * SQL in this app is routinely written flush-left (`WHERE id IN (…)` at column
 * 1), so `lineHasSpace` is false and Monaco relocates the bulb to the PREVIOUS
 * line — still at column 1. The bulb then paints directly on top of the first
 * character of an unrelated line (`FROM orders` → a yellow blob over the `F`).
 * Monaco's only mitigation is a 30%-opacity backdrop, which is far too weak to
 * separate a solid amber glyph from the syntax-coloured text underneath.
 *
 * The bulb cannot simply be nudged left with CSS: the content-widget layer
 * lives inside Monaco's scrollable element, which is positioned at
 * `layoutInfo.contentLeft` with `overflow: hidden`, so anything translated into
 * the gutter is clipped away.
 *
 * THE FIX
 * -------
 * Hide Monaco's content-widget bulb and mirror its visibility into a
 * LINES-DECORATIONS gutter decoration on the action's own line. Clicking it
 * runs the same `editor.action.quickFix` command Monaco's bulb ran, so the
 * action menu, the Ctrl/Cmd+. keybinding and the post-apply analytics path are
 * all untouched.
 *
 * WHY THE LINES-DECORATIONS LANE AND NOT THE GLYPH MARGIN
 * ------------------------------------------------------
 * Measured layout of a Query Editor at fontSize 14 (`editor.getLayoutInfo()`):
 *
 *     glyphMarginLeft   0   glyphMarginWidth  21   <- taken, see below
 *     lineNumbersLeft  21   lineNumbersWidth  42
 *     decorationsLeft  63   decorationsWidth  26   <- the bulb lives here
 *     contentLeft      89                          <- code text starts here
 *
 * The glyph margin looks like the natural home, but this app already parks
 * `CursorStatementGutterActions` (the Run / Peek / Edit buttons) there — an
 * absolutely-positioned overlay at `left: 2` with a lineHeight-square button
 * stack anchored to the cursor statement's FIRST line. A code action offered on
 * that same line would sit underneath it. The lines-decorations lane sits
 * between the line numbers and `contentLeft`, is otherwise empty, and is
 * flush against the code — so the bulb reads as belonging to its line while
 * still being structurally incapable of overlapping any text.
 *
 * Co-tenant to be aware of: Monaco's folding chevrons also render in this lane,
 * but they are `opacity: 0` until the margin is hovered or a region is
 * collapsed, and flush-left SQL produces no indentation-based fold ranges.
 *
 * The bulb is drawn as an inline SVG (same pattern as `debugStyles.ts`) rather
 * than the `codicon-light-bulb` font glyph: at gutter size the codicon reads as
 * an amorphous dot, and an SVG lets us control silhouette and contrast in both
 * themes without depending on the codicon webfont having loaded.
 */

import type { editor as monacoEditor } from 'monaco-editor';

import type { IStandaloneCodeEditor, Monaco } from './sqlMonacoTypes';

/** Class Monaco writes on the gutter decoration element. */
export const CODE_ACTION_LIGHTBULB_CLASS = 'jam-code-action-lightbulb';
const GLYPH_CLASS = CODE_ACTION_LIGHTBULB_CLASS;
/**
 * Marker class added to Monaco's own content-widget bulb so the stylesheet can
 * hide it. Applied from JS (not a blanket CSS rule) so that if this module ever
 * fails to attach, Monaco's built-in bulb keeps rendering — degraded placement
 * beats no affordance at all.
 */
const SOURCE_CLASS = 'jam-code-action-lightbulb-source';
/** Monaco's editor-contribution id for `LightBulbWidget` (monaco 0.44). */
const LIGHTBULB_CONTRIBUTION_ID = 'editor.contrib.lightbulbWidget';
/** Attribute Monaco's content-widget layer sets while a widget is on screen. */
const VISIBLE_ATTR = 'monaco-visible-content-widget';

/** Minimal shape of Monaco's `LightBulbWidget` this module reads. */
type LightbulbContribution = {
  readonly getDomNode?: () => HTMLElement | null;
  /** `LightBulbState.Showing` carries the position the ACTION belongs to,
   *  which is what we decorate — as opposed to the shifted widget position. */
  readonly state?: { readonly editorPosition?: { readonly lineNumber?: number } };
};

/** Editor surface this module needs beyond the app's shared handle. */
type LightbulbHostEditor = IStandaloneCodeEditor & {
  readonly getContribution?: (id: string) => unknown;
  readonly createDecorationsCollection?: (
    decorations?: monacoEditor.IModelDeltaDecoration[],
  ) => monacoEditor.IEditorDecorationsCollection;
  readonly onMouseDown?: (
    listener: (e: monacoEditor.IEditorMouseEvent) => void,
  ) => { dispose: () => void };
  readonly getPosition?: () => { readonly lineNumber: number } | null;
};

const bulbSvg = (glass: string, ink: string): string =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E` +
  `%3Ccircle cx='8' cy='6' r='5' fill='${glass}'/%3E` +
  `%3Crect x='5.5' y='9.5' width='5' height='3' fill='${glass}'/%3E` +
  `%3Crect x='5.6' y='12.6' width='4.8' height='1.3' rx='0.65' fill='${ink}'/%3E` +
  `%3Crect x='6.4' y='14.4' width='3.2' height='1.3' rx='0.65' fill='${ink}'/%3E` +
  `%3C/svg%3E")`;

/**
 * The stylesheet, exported so an E2E can inject it into a live editor and
 * assert the rule actually MATCHES Monaco's decoration element. That assertion
 * is load-bearing: Monaco owns the element's size and position, so a
 * geometry-only test passes even when the icon paints nothing — which is
 * exactly how a first cut of this fix (selector scoped to the wrong Monaco
 * container) rendered a blank lane past a green test.
 */
export const CODE_ACTION_LIGHTBULB_CSS = `
    /* Monaco's own content-widget bulb — replaced by the gutter glyph below.
       Scoped to the widget instances this module successfully attached to. */
    .monaco-editor .lightBulbWidget.${SOURCE_CLASS} { display: none !important; }

    /* Monaco 0.44 renders \`linesDecorationsClassName\` decorations as
       \`div.cldr.<class>\` inside \`.margin-view-overlays\`, inline-styled to
       \`left: decorationsLeft; width: decorationsWidth\` and the line's height —
       so the icon only needs centering. Deliberately NOT scoped to that
       container: the class is app-private, and keeping the selector container-
       agnostic means a Monaco upgrade that moves the lane cannot silently
       blank the glyph again. */
    .monaco-editor .${GLYPH_CLASS} {
      background-image: ${bulbSvg('%23ddb100', '%238a6d00')};
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 14px 14px;
      cursor: pointer;
      opacity: 0.92;
      transition: opacity 120ms ease-out, transform 120ms ease-out;
    }
    .monaco-editor .${GLYPH_CLASS}:hover {
      opacity: 1;
      transform: scale(1.12);
    }
    .dark .monaco-editor .${GLYPH_CLASS} {
      background-image: ${bulbSvg('%23ffcc00', '%23a37f00')};
    }
    @media (prefers-reduced-motion: reduce) {
      .monaco-editor .${GLYPH_CLASS} { transition: none; }
      .monaco-editor .${GLYPH_CLASS}:hover { transform: none; }
    }
  `;

let stylesInjected = false;

/** Injects the gutter stylesheet once per page-lifetime. */
export function injectCodeActionLightbulbStyles(): void {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.dataset.jamStyles = 'code-action-lightbulb';
  style.textContent = CODE_ACTION_LIGHTBULB_CSS;
  document.head.appendChild(style);
}

/** Test-only: lets a suite re-inject after clearing `document.head`. */
export function _resetCodeActionLightbulbStylesForTesting(): void {
  stylesInjected = false;
}

/**
 * Moves Monaco's code-action lightbulb out of the text and into the gutter
 * lane of the line the action belongs to.
 *
 * Returns a disposable. A no-op disposable is returned when the Monaco build
 * does not expose the pieces we need — in that case Monaco keeps rendering its
 * own bulb unchanged.
 */
export function attachCodeActionLightbulb(
  editor: LightbulbHostEditor,
  monaco: Monaco,
): { dispose: () => void } {
  const noop = { dispose: () => {} };
  if (typeof MutationObserver === 'undefined') return noop;

  injectCodeActionLightbulbStyles();

  let contribution: LightbulbContribution | null = null;
  try {
    contribution = (editor.getContribution?.(LIGHTBULB_CONTRIBUTION_ID) ??
      null) as LightbulbContribution | null;
  } catch {
    contribution = null;
  }
  const widgetNode = contribution?.getDomNode?.() ?? null;
  const collection = editor.createDecorationsCollection?.([]);
  if (!widgetNode || !collection) return noop;

  widgetNode.classList.add(SOURCE_CLASS);

  let decoratedLine = 0;

  const actionLine = (): number => {
    const fromState = contribution?.state?.editorPosition?.lineNumber;
    if (typeof fromState === 'number' && fromState > 0) return fromState;
    // Fallback: Monaco's CodeActionModel always triggers at the cursor, so the
    // cursor's line is the action's line whenever the bulb is showing.
    return editor.getPosition?.()?.lineNumber ?? 0;
  };

  const sync = (): void => {
    const line = widgetNode.hasAttribute(VISIBLE_ATTR) ? actionLine() : 0;
    if (line === decoratedLine) return;
    decoratedLine = line;
    if (line === 0) {
      collection.clear();
      return;
    }
    collection.set([
      {
        range: new monaco.Range(line, 1, line, 1),
        options: {
          // No tooltip option exists for this lane in Monaco 0.44
          // (`glyphMarginHoverMessage` is glyph-margin-only) and the element is
          // re-created from an HTML string on every render pass, so a JS-set
          // `title` would not survive. `cursor: pointer` carries the
          // affordance; Ctrl/Cmd+. remains the discoverable keyboard path.
          linesDecorationsClassName: GLYPH_CLASS,
          stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
        },
      },
    ]);
  };

  const observer = new MutationObserver(sync);
  observer.observe(widgetNode, {
    attributes: true,
    attributeFilter: [VISIBLE_ATTR, 'style', 'class'],
  });

  const mouseDown = editor.onMouseDown?.((e) => {
    if (decoratedLine === 0) return;
    if (e.target?.type !== monaco.editor.MouseTargetType.GUTTER_LINE_DECORATIONS) return;
    if (e.target?.position?.lineNumber !== decoratedLine) return;
    e.event?.preventDefault?.();
    e.event?.stopPropagation?.();
    editor.focus();
    void Promise.resolve(editor.getAction('editor.action.quickFix')?.run()).catch(() => {});
  });

  return {
    dispose: () => {
      observer.disconnect();
      mouseDown?.dispose();
      collection.clear();
      widgetNode.classList.remove(SOURCE_CLASS);
    },
  };
}
